/**
 * Date/Time conversion utilities using dayjs for proper timezone handling
 * 
 * SINGLE CONTRACT:
 * - DB stores game.date_time as UTC ISO with Z
 * - DB stores game.tz as IANA tz
 * - Save: datetime-local string + tz → UTC ISO
 * - Render input: UTC ISO + tz → YYYY-MM-DDTHH:mm
 * - Display: UTC ISO + tz → pretty string
 */
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import tzPlugin from 'dayjs/plugin/timezone';
import { getPreviewTz } from './previewTz';

dayjs.extend(utc);
dayjs.extend(tzPlugin);

/**
 * Convert UTC ISO string to local datetime-local input format
 * For <input type="datetime-local"> value
 */
export const fromUtcToLocalInput = (utcIso, ianaTz) => {
  if (!utcIso) return "";
  const d = dayjs.tz(utcIso, "UTC").tz(ianaTz || dayjs.tz.guess());
  return d.format("YYYY-MM-DDTHH:mm"); // IMPORTANT: no timezone suffix
};

/**
 * Convert local input to UTC ISO string
 * For payload to server (UTC ISO string)
 */
export const toUtcIso = (localInput, ianaTz) => {
  if (!localInput) return null;
  const d = dayjs.tz(localInput, "YYYY-MM-DDTHH:mm", ianaTz || dayjs.tz.guess());
  return d.utc().toISOString(); // ends with Z
};

/**
 * Format UTC ISO for display in local timezone
 * For card/detail display
 */
export const formatLocalDisplay = (utcIso, ianaTz) => {
  if (!utcIso) return "";
  return dayjs.tz(utcIso, "UTC").tz(ianaTz || dayjs.tz.guess()).format("MMM D, YYYY h:mm A");
};

/**
 * Get user's timezone (IANA format)
 * Supports preview override via ?force_tz query param
 */
export const getUserTimezone = () => {
  return getPreviewTz() || dayjs.tz.guess();
};

/**
 * Parse a timestamp string as UTC.
 * Backend timestamps may omit the 'Z' suffix, causing new Date() to treat
 * them as local time.  This ensures they're always parsed as UTC.
 */
export const parseUTC = (timestamp) => {
  if (!timestamp) return null;
  const str = String(timestamp);
  // If it already has a timezone indicator (Z, +, -), parse as-is
  if (str.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(str)) {
    return new Date(str);
  }
  // Append Z so new Date() treats it as UTC
  return new Date(str + 'Z');
};

/**
 * Returns true when a run's scheduled start time is strictly before "now".
 *
 * Mirrors backend `_run_is_past` (server.py) semantics exactly:
 *  - null / missing / empty           -> false
 *  - unparseable / invalid date       -> false
 *  - naive strings treated as UTC     (via parseUTC appending 'Z')
 *  - 'Z' and offset strings handled   (via parseUTC)
 *  - STRICT comparison: past only when start < now (never <=)
 */
export const isPastRun = (dateTime) => {
  const d = parseUTC(dateTime);
  if (!d || isNaN(d.getTime())) return false;
  return d.getTime() < Date.now();
};

// Backward compatibility
export const userTz = getUserTimezone();
export { formatLocalDisplay as formatLocalDateTime };
